/**
 * WorkbenchFilesTab — TASK-606 Door 1: the Documents page's real file hub.
 * Lists versioned workbench documents (Open → /workbench/:docId) and the
 * Deliverables section (TASK-741). Creation goes through the ONE
 * CreateArtifactDialog (TASK-746) — the per-surface New▾/template/upload menus
 * are deleted (hard cutover).
 *
 * TASK-818: the two flat, account-wide lists are split into one section per client, and
 * filtering became a set of selections instead of a text box you had to know what to type
 * into. Nine identically-named "brief deck" rows with no client made the old list close to
 * unreadable — the work is organised by client, so the page is too.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderOpen, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  listWorkbenchDocuments,
  type WorkbenchDocumentSummary,
} from '../../services/api/documentApi';
import {
  listExports,
  retryExport,
  type ExportListItem,
} from '../../services/api/exportApi';
import { clientService } from '../../services/clientService';
import { groupFailedExports } from '../../utils/rmSafeError';
import {
  clientOptions,
  formatOptions,
  groupClientFiles,
  EMPTY_FILTERS,
  type FileFilters,
} from '../../utils/groupClientFiles';
import { logger } from '../../utils/logger';
import { WidgetErrorState } from '../ui/WidgetErrorState';
import CreateArtifactDialog from './CreateArtifactDialog';
import ClientFilesGroup from './files/ClientFilesGroup';
import FilesFilterBar from './files/FilesFilterBar';
import { formatIcon } from './files/filePresentation';

/** Enough to name every client that owns a document; the account cap is far below this. */
const CLIENT_PAGE_SIZE = 200;

const EXPORT_TYPE_LABEL: Record<string, string> = {
  brief: 'Meeting brief',
  portfolio: 'Portfolio review',
  finsa: 'FinSA documentation',
};

export const WorkbenchFilesTab: React.FC = () => {
  const navigate = useNavigate();
  const [docs, setDocs] = useState<WorkbenchDocumentSummary[]>([]);
  const [exports, setExports] = useState<ExportListItem[]>([]);
  const [clientNames, setClientNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FileFilters>(EMPTY_FILTERS);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  // TASK-746: creation is the one shared dialog.
  const [createOpen, setCreateOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // TASK-741: deliverables are their own section now — list recent exports
      // of EVERY status; failed ones no longer sit inline with working files.
      // TASK-818: clients come too, because an export carries a client_id and no name —
      // without this every deliverable groups under a raw id.
      const [docsRes, exportsRes, clientsRes] = await Promise.all([
        listWorkbenchDocuments(),
        listExports({ limit: 50 }).catch(() => ({ exports: [], total: 0 })),
        clientService
          .fetchClients(0, CLIENT_PAGE_SIZE)
          .catch((err) => {
            // Losing the names is survivable — the ids still group correctly and every
            // document stays reachable. Losing the documents is not, so this never throws.
            logger.warn('[WorkbenchFilesTab] Could not resolve client names:', err);
            return { clients: [], total: 0, has_more: false };
          }),
      ]);
      setDocs(docsRes.documents);
      setExports(exportsRes.exports);
      setClientNames(new Map(clientsRes.clients.map((c) => [c.id, c.name])));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const openDoc = (docId: string) => navigate(`/workbench/${docId}`);

  const handleRetry = async (item: ExportListItem) => {
    setBusy(true);
    try {
      const result = await retryExport(item);
      if (result === null) {
        // m8 (epic review): a fall-through retry did nothing — say so.
        toast.error('This deliverable cannot be retried automatically.');
        return;
      }
      toast.success('Regeneration started — check back shortly');
      void fetchAll();
    } catch (err) {
      toast.error(`Retry failed: ${err instanceof Error ? err.message : 'unknown error'}`);
    } finally {
      setBusy(false);
    }
  };

  // TASK-652: repeated failed attempts of the same document/action render as one
  // grouped row with RM-safe copy; raw diagnostics only reach the console.
  const failedExports = useMemo(() => exports.filter((e) => e.status === 'failed'), [exports]);
  const readyExports = useMemo(() => exports.filter((e) => e.status === 'ready'), [exports]);
  const groupedFailures = useMemo(() => groupFailedExports(failedExports), [failedExports]);
  useEffect(() => {
    if (failedExports.length > 0) {
      console.debug('[workbench] raw failed exports (diagnostics):', failedExports);
    }
  }, [failedExports]);

  // TASK-741: "based on vN" derives from READY export rows only. The source
  // title resolves through the working-documents list where possible.
  const docTitle = useCallback(
    (id: string | null | undefined) =>
      (id && docs.find((d) => d.document_id === id)?.title) || null,
    [docs]
  );

  const deliverableLabel = useCallback(
    (e: ExportListItem) => {
      if (e.export_type === 'workbench') {
        const title = docTitle(e.source_document_id);
        const base = title ?? 'Workbench document';
        return e.source_document_version != null
          ? `${base} · ${String(e.format ?? 'file').toUpperCase()} deliverable · based on v${e.source_document_version}`
          : `${base} · ${String(e.format ?? 'file').toUpperCase()} deliverable`;
      }
      // TASK-818: the old label was `${export_type} deck`, which rendered nine consecutive
      // rows reading "brief deck" with nothing to tell them apart. The client now comes
      // from the group heading; the date and version carry the rest.
      return EXPORT_TYPE_LABEL[e.export_type] ?? `${e.export_type} deck`;
    },
    [docTitle]
  );

  const clients = useMemo(
    () => clientOptions(docs, readyExports, clientNames),
    [docs, readyExports, clientNames]
  );
  const formats = useMemo(() => formatOptions(docs, readyExports), [docs, readyExports]);

  const groups = useMemo(
    () =>
      groupClientFiles({
        documents: docs,
        deliverables: readyExports,
        names: clientNames,
        deliverableLabel,
        filters,
      }),
    [docs, readyExports, clientNames, deliverableLabel, filters]
  );

  const toggleGroup = (clientId: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });

  if (loading) {
    return (
      <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-6" aria-hidden>
        <div className="h-8 animate-pulse rounded bg-gray-100" />
        <div className="h-8 animate-pulse rounded bg-gray-100" />
        <div className="h-8 animate-pulse rounded bg-gray-100" />
      </div>
    );
  }

  if (error) {
    return (
      <WidgetErrorState
        title="Couldn't load workbench documents"
        detail={error}
        onRetry={() => void fetchAll()}
      />
    );
  }

  const nothingAtAll = docs.length === 0 && readyExports.length === 0;
  const shownFiles = groups.reduce((sum, g) => sum + g.total, 0);

  return (
    <div className="space-y-3" data-testid="workbench-files-tab">
      <FilesFilterBar
        filters={filters}
        onChange={setFilters}
        clients={clients}
        formats={formats}
        onCreate={() => setCreateOpen(true)}
        createDisabled={busy}
      />

      {/* TASK-652 / TASK-818: failures stay ABOVE the client groups rather than inside
          them. They are the one thing on this tab that needs a person, and a collapsed
          client section is a good way to never see them again. */}
      {groupedFailures.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <h4 className="mb-2 text-sm font-semibold text-red-800">
            {groupedFailures.length} generation{groupedFailures.length === 1 ? '' : 's'} failed
          </h4>
          <div className="space-y-1">
            {groupedFailures.map((group) => (
              <div key={group.key} className="flex items-center gap-2 text-sm">
                {formatIcon(group.latest.format ?? 'pptx')}
                <span className="font-medium capitalize text-gray-800">
                  {deliverableLabel(group.latest)}
                  {group.count > 1 ? ` · ${group.count} attempts` : ''}
                </span>
                <span className="min-w-0 truncate text-xs text-red-700">{group.message}</span>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void handleRetry(group.latest)}
                  className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-md border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
                >
                  <RotateCw className="h-3 w-3" /> Retry
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {groups.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
          <FolderOpen className="mx-auto mb-3 h-10 w-10 text-gray-300" />
          <p className="text-sm text-gray-500">
            {nothingAtAll
              ? 'No documents yet — create one with New, or generate a deliverable from a meeting.'
              : 'No documents match these filters.'}
          </p>
          {!nothingAtAll && (
            <button
              type="button"
              onClick={() => setFilters({ ...EMPTY_FILTERS })}
              className="mt-3 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-[#002B45] hover:bg-gray-50"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          <p className="px-1 text-xs text-gray-500" data-testid="files-group-summary">
            {shownFiles} file{shownFiles === 1 ? '' : 's'} across {groups.length} client
            {groups.length === 1 ? '' : 's'}
          </p>
          <div className="space-y-2">
            {groups.map((group) => (
              <ClientFilesGroup
                key={group.clientId}
                group={group}
                expanded={!collapsed.has(group.clientId)}
                onToggle={() => toggleGroup(group.clientId)}
                onOpenDocument={openDoc}
                deliverableLabel={deliverableLabel}
              />
            ))}
          </div>
        </>
      )}

      {/* TASK-746: the one create surface (template picker lifted into it) */}
      <CreateArtifactDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => void fetchAll()}
      />
    </div>
  );
};

export default WorkbenchFilesTab;
